import { Add, Cancel, CheckCircle, Delete, Edit, LockOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Stack,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  AdminDialog,
  AdminEmptyState,
  AdminFilterPanel,
  AdminTableActions,
  AdminTableBodyState,
  AdminTableFrame,
  AppChip,
  ErrorState,
  PageShell,
} from "@/components/common";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { useTableSorting } from "@/hooks/useTableSorting";
import { paths } from "@/routes/paths";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import {
  CustomerSsoProvider,
  platformsOf,
  SSO_PLATFORM_LABELS,
  SSO_PROTOCOL_LABELS,
  SsoProtocol,
  SsoProviderService,
} from "@/services/features/SsoProviderService";

const protocolLogos: Record<SsoProtocol, string> = {
  OIDC: "/images/protocols/openid.svg",
  SAML: "/images/protocols/saml.svg",
};

type LoadStatus = "loading" | "error" | "ready";
type ProviderColumn = "name" | "protocol" | "platforms" | "status";
const columns: ProviderColumn[] = ["status", "name", "protocol", "platforms"];
const platformValue = (provider: CustomerSsoProvider) =>
  platformsOf(provider)
    .map((platform) => SSO_PLATFORM_LABELS[platform])
    .join(" · ");

export default function SsoProviders() {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [revision, setRevision] = useState(0);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    setStatus("loading");
    const load = async () => {
      try {
        const result = await CustomerService.getInstance().getAllCustomers();
        if (!result.success || !result.data) throw new Error("Unable to load customers");
        if (active) {
          setCustomers(result.data.toSorted((a, b) => a.name.localeCompare(b.name)));
          setStatus("ready");
        }
      } catch {
        if (active) setStatus("error");
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [revision]);

  const customer = customers.find((entry) => entry.id === Number(params.get("customerId"))) ?? null;

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.sso.title")}
      icon={<LockOutlined />}
      headerActions={
        <Button
          variant="contained"
          startIcon={<Add />}
          disabled={!customer || status !== "ready"}
          onClick={() => {
            if (customer) void navigate(`${paths.admin.sso}/${customer.id}/new`);
          }}
        >
          {t("management.sso.actions.addProvider")}
        </Button>
      }
      contextControls={
        <AdminFilterPanel>
          <MuiAutocompleteMultiSelect
            multiSelect={false}
            label={t("common.customer")}
            placeholder={t("management.sso.empty.selectCustomer")}
            options={customers}
            value={customer ? [customer] : []}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option.id}
            onChange={(next) => setParams(next[0] ? { customerId: String(next[0].id) } : {})}
            loading={status === "loading"}
            disabled={status !== "ready"}
          />
        </AdminFilterPanel>
      }
    >
      <CustomerProviders
        key={customer?.id ?? "unselected"}
        customer={customer}
        customersStatus={status}
        onRetryCustomers={() => setRevision((value) => value + 1)}
      />
    </PageShell>
  );
}

interface CustomerProvidersProps {
  customer: Customer | null;
  customersStatus: LoadStatus;
  onRetryCustomers: () => void;
}

function CustomerProviders({
  customer,
  customersStatus,
  onRetryCustomers,
}: CustomerProvidersProps) {
  const { t, i18n } = useTranslation();
  const [providers, setProviders] = useState<CustomerSsoProvider[]>([]);
  const [status, setStatus] = useState<LoadStatus>(customer ? "loading" : "ready");
  const [revision, setRevision] = useState(0);
  const [deleting, setDeleting] = useState<CustomerSsoProvider | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const customerId = customer?.id;
  const customersReady = customersStatus === "ready";

  useEffect(() => {
    if (!customerId || !customersReady) return;
    let active = true;
    setStatus("loading");
    void SsoProviderService.getInstance()
      .getProviders(customerId)
      .then((result) => {
        if (active) {
          setProviders(result);
          setStatus("ready");
        }
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [customerId, customersReady, revision]);

  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting(
    providers,
    (provider, column: ProviderColumn) => {
      switch (column) {
        case "name":
          return provider.name;
        case "protocol":
          return SSO_PROTOCOL_LABELS[provider.protocol] ?? provider.protocol;
        case "platforms":
          return platformValue(provider);
        case "status":
          return t(provider.enabled ? "management.sso.enabled" : "management.sso.disabled");
      }
    },
    "name",
    i18n.resolvedLanguage ?? i18n.language,
  );

  const deleteProvider = async () => {
    if (!deleting || !customer || busy) return;
    setBusy(true);
    setDeleteError("");
    try {
      await SsoProviderService.getInstance().deleteProvider(customer.id, deleting.id);
      setProviders((current) => current.filter((entry) => entry.id !== deleting.id));
      setDeleting(null);
      enqueueSnackbar(t("management.sso.deleted"), { variant: "success" });
    } catch {
      setDeleteError(t("management.sso.errors.delete"));
    } finally {
      setBusy(false);
    }
  };

  const failed = customersStatus === "error" || status === "error";
  const emptyState = failed ? (
    <ErrorState
      title={t(
        customersStatus === "error"
          ? "management.sso.errors.customers"
          : "management.sso.errors.providers",
      )}
      onRetry={
        customersStatus === "error" ? onRetryCustomers : () => setRevision((value) => value + 1)
      }
    />
  ) : (
    <AdminEmptyState
      icon={<LockOutlined sx={{ fontSize: 40 }} />}
      text={t(
        customer ? "management.sso.empty.noProviders" : "management.sso.empty.selectCustomer",
      )}
      description={t(
        customer ? "management.sso.empty.addProvider" : "management.sso.empty.selectDescription",
      )}
    />
  );

  return (
    <>
      <AdminTableFrame
        tableSx={{
          "& .MuiTableHead-root .MuiTableCell-root": {
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          },
          "& .MuiTableHead-root .MuiTableCell-root:first-of-type": {
            borderInlineStart: (theme) => `1px solid ${theme.palette.divider}`,
          },
          "& .MuiTableHead-root .MuiTableCell-root:last-of-type": {
            borderInlineEnd: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <SortableTableCell
                key={column}
                column={column}
                activeColumn={sortColumn}
                direction={sortDirection}
                onSort={handleSort}
                sx={{ width: column === "protocol" ? "100%" : "1%", whiteSpace: "nowrap" }}
              >
                {t(`management.sso.columns.${column}`)}
              </SortableTableCell>
            ))}
            <TableCell sx={{ width: "1%", whiteSpace: "nowrap", textAlign: "end" }}>
              {t("management.sso.columns.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <AdminTableBodyState
          items={failed ? [] : sortedItems}
          isLoading={
            customersStatus === "loading" ||
            (Boolean(customer) && customersReady && status === "loading")
          }
          colSpan={columns.length + 1}
          loadingLabel={t(
            customersStatus === "loading"
              ? "management.sso.loading.customers"
              : "management.sso.loading.providers",
          )}
          emptyState={emptyState}
        >
          {(provider) => (
            <TableRow
              key={provider.id}
              hover
              tabIndex={0}
              onClick={() => void navigate(`${paths.admin.sso}/${customerId}/${provider.id}`)}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  void navigate(`${paths.admin.sso}/${customerId}/${provider.id}`);
                }
              }}
              sx={{
                cursor: "pointer",
                "&:focus-visible": {
                  outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: -2,
                },
              }}
            >
              <TableCell align="center">
                <Tooltip
                  title={t(provider.enabled ? "management.sso.enabled" : "management.sso.disabled")}
                >
                  <Box
                    component="span"
                    role="img"
                    aria-label={t(
                      provider.enabled ? "management.sso.enabled" : "management.sso.disabled",
                    )}
                    sx={{ display: "inline-flex" }}
                  >
                    {provider.enabled ? (
                      <CheckCircle color="success" sx={{ fontSize: 18 }} />
                    ) : (
                      <Cancel color="error" sx={{ fontSize: 18 }} />
                    )}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{provider.name}</TableCell>
              <TableCell>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                  {protocolLogos[provider.protocol] && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark" ? "common.white" : "transparent",
                      }}
                    >
                      <Box
                        component="img"
                        src={protocolLogos[provider.protocol]}
                        alt=""
                        aria-hidden="true"
                        sx={{
                          display: "block",
                          height: 24,
                          width: 24,
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    {SSO_PROTOCOL_LABELS[provider.protocol] ?? provider.protocol}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Stack spacing={0.5} alignItems="flex-start">
                  {platformsOf(provider).map((platform) => (
                    <AppChip
                      key={platform}
                      tone="neutral"
                      size="small"
                      label={SSO_PLATFORM_LABELS[platform]}
                    />
                  ))}
                </Stack>
              </TableCell>
              <TableCell>
                <AdminTableActions
                  actions={[
                    {
                      icon: <Edit />,
                      hint: t("management.sso.actions.editProvider"),
                      ariaLabel: t("management.sso.actions.editNamed", { name: provider.name }),
                      action: () =>
                        void navigate(`${paths.admin.sso}/${customerId}/${provider.id}`),
                    },
                    {
                      icon: <Delete />,
                      hint: t("management.sso.actions.deleteProvider"),
                      ariaLabel: t("management.sso.actions.deleteNamed", { name: provider.name }),
                      color: "error",
                      action: () => {
                        setDeleteError("");
                        setDeleting(provider);
                      },
                    },
                  ]}
                />
              </TableCell>
            </TableRow>
          )}
        </AdminTableBodyState>
      </AdminTableFrame>
      <AdminDialog
        open={deleting !== null}
        title={t("management.sso.deleteTitle")}
        onClose={() => {
          if (!busy) setDeleting(null);
        }}
        error={deleteError}
        cancelAction={{
          label: t("common.cancel"),
          onClick: () => setDeleting(null),
          disabled: busy,
        }}
        primaryAction={{
          label: t("management.sso.actions.deleteProvider"),
          color: "error",
          onClick: () => void deleteProvider(),
          loading: busy,
        }}
      >
        <Typography>
          {t("management.sso.confirmDelete", {
            name: deleting?.name ?? "",
            customer: customer?.name ?? "",
          })}
        </Typography>
      </AdminDialog>
    </>
  );
}
